import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Label } from '@atlaskit/form';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages';

type SiteNameProps = {
	siteName: string | null;
	isLoading?: boolean;
	hasErrorOccurred?: boolean;
	onFetchSiteName?: () => void;
};

const SiteNameContent = ({
	siteName,
	isLoading,
	hasErrorOccurred,
	onFetchSiteName,
}: SiteNameProps) => {
	const { formatMessage } = useIntl();

	if (isLoading) {
		return (
			<SpinnerWrapper data-testId="project-create-importer-upload-flow.common.ui.site-name.spinner-wrapper">
				<Spinner size="small" />
			</SpinnerWrapper>
		);
	}

	if (hasErrorOccurred) {
		return (
			<RetryWrapper>
				<Button appearance="link" spacing="none" onClick={onFetchSiteName}>
					{formatMessage(messages.retry)}
				</Button>
			</RetryWrapper>
		);
	}

	return <StyledSiteName>{siteName}</StyledSiteName>;
};

export const SiteName = (props: SiteNameProps) => {
	const { formatMessage } = useIntl();

	return (
		<>
			<Label htmlFor="current-account">{formatMessage(messages.currentAccount)}</Label>
			<Wrapper hasErrorOccurred={props.hasErrorOccurred}>
				<SiteNameContent {...props} />
			</Wrapper>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ hasErrorOccurred?: boolean }>({
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.disabled', colors.N20),
	height: '40px',
	borderRadius: '4px',
	marginBottom: token('space.050', '4px'),
	paddingLeft: token('space.100', '8px'),
	paddingRight: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: ({ hasErrorOccurred }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		hasErrorOccurred ? `2px solid ${token('color.border.danger', colors.R400)}` : 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledSiteName = styled.div({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.div({
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RetryWrapper = styled.div({
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});
